import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../home/components/Navbar";
import Footer from "../../home/components/Footer";
import SEOHead from "@/components/feature/SEOHead";
import { blogPosts } from "../../../mocks/blogData";

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeChapter, setActiveChapter] = useState<string>("");

  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    if (post && post.chapters.length > 0) {
      setActiveChapter(post.chapters[0].id);
    }
  }, [post]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-widest uppercase mb-4">
            [ Signal Lost ]
          </div>
          <h1 className="font-['Special_Elite'] text-white text-4xl mb-4">Entry Not Found</h1>
          <p className="font-['Rajdhani'] text-[#555] mb-8">This transmission could not be recovered.</p>
          <button
            onClick={() => navigate("/lore")}
            className="flex items-center gap-2 mx-auto border border-[#C8922A]/50 hover:border-[#C8922A] hover:bg-[#C8922A]/10 text-[#C8922A] text-xs tracking-[0.15em] uppercase font-['Share_Tech_Mono'] px-5 py-3 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Back to Archive
          </button>
        </div>
      </div>
    );
  }

  const currentChapter = post.chapters.find((c) => c.id === activeChapter);
  const currentIndex = post.chapters.findIndex((c) => c.id === activeChapter);

  // Render a single content chunk with smart type detection
  const renderParagraph = (para: string, i: number, characterColor: string) => {
    // Section header: ## Title
    if (para.startsWith("## ")) {
      const text = para.slice(3);
      return (
        <h3
          key={i}
          className="font-['Special_Elite'] text-white text-xl md:text-2xl mt-10 mb-2 pt-8 border-t border-[#1A1A1A] first:mt-0 first:pt-0 first:border-t-0"
        >
          {text}
        </h3>
      );
    }

    // Speaker label: SPEAKER:Name
    if (para.startsWith("SPEAKER:")) {
      const name = para.slice(8);
      return (
        <div
          key={i}
          className="font-['Share_Tech_Mono'] text-xs tracking-[0.25em] uppercase mt-6 mb-1"
          style={{ color: characterColor }}
        >
          — {name}
        </div>
      );
    }

    // Dialogue / blockquote: > "text"
    if (para.startsWith("> ")) {
      const text = para.slice(2);
      return (
        <blockquote
          key={i}
          className="border-l-2 pl-5 py-1 my-2"
          style={{ borderColor: characterColor }}
        >
          <p className="font-['Special_Elite'] text-[#D0C4A8] text-base md:text-lg leading-[1.85] italic">
            {text}
          </p>
        </blockquote>
      );
    }

    // Callout / emphasis box: !! Text
    if (para.startsWith("!! ")) {
      const text = para.slice(3);
      return (
        <div
          key={i}
          className="my-6 px-6 py-5 border-l-2"
          style={{
            borderColor: characterColor,
            backgroundColor: `${characterColor}0D`,
          }}
        >
          <p
            className="font-['Special_Elite'] text-base md:text-lg leading-[1.85] italic"
            style={{ color: characterColor }}
          >
            {text}
          </p>
        </div>
      );
    }

    // Default: regular body paragraph
    return (
      <p
        key={i}
        className="font-['Rajdhani'] text-[#AAAAAA] leading-[1.9] text-base md:text-lg"
      >
        {para}
      </p>
    );
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${post.title} — ${post.subtitle}`,
    "description": post.excerpt,
    "url": `${import.meta.env.VITE_SITE_URL || ""}/lore/${post.slug}`,
    "image": post.coverImage,
    "datePublished": post.date,
    "inLanguage": "en",
    "isPartOf": {
      "@type": "Blog",
      "name": "The Wraith Lore Archive",
      "url": `${import.meta.env.VITE_SITE_URL || ""}/lore`,
    },
    "about": {
      "@type": "Person",
      "name": post.character,
      "alternateName": post.characterAlias,
    },
    "isAccessibleForFree": true,
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEOHead
        title={`${post.title} | ${post.subtitle} — The Wraith Lore Archive`}
        description={post.excerpt}
        keywords={`${post.character}, The Wraith lore, ${post.characterTag}, post-apocalyptic character history, Green Monday, The Wraith Chronicles`}
        canonical={`/lore/${post.slug}`}
        ogTitle={`${post.title} — ${post.subtitle}`}
        ogDescription={post.excerpt}
        ogType="article"
        ogImage={post.coverImage}
        schema={articleSchema}
      />

      <Navbar />

      {/* Cover Hero */}
      <header className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
        <img
          src={post.coverImage}
          alt={`${post.title} cover`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-black/30" />

        <div className="absolute bottom-0 left-0 right-0 px-6 pb-10">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate("/lore")}
              className="flex items-center gap-2 font-['Share_Tech_Mono'] text-[#666] hover:text-[#C8922A] text-xs tracking-[0.2em] uppercase transition-colors duration-300 cursor-pointer mb-6"
            >
              <i className="ri-arrow-left-line text-sm" />
              Lore Archive
            </button>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span
                className="font-['Share_Tech_Mono'] text-xs tracking-[0.25em] uppercase px-3 py-1.5 border"
                style={{ color: post.characterColor, borderColor: `${post.characterColor}50`, backgroundColor: `${post.characterColor}15` }}
              >
                {post.characterTag}
              </span>
              <span className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-widest">
                {post.entryNumber}
              </span>
              <span className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-widest">
                {post.timeline}
              </span>
            </div>

            <h1 className="font-['Special_Elite'] text-white text-4xl md:text-6xl mb-2">
              {post.title}
            </h1>
            <div className="font-['Rajdhani'] text-[#777] text-lg tracking-wide">
              {post.subtitle}
            </div>
          </div>
        </div>
      </header>

      {/* Excerpt Banner */}
      <div className="w-full px-6 py-8 border-b border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto">
          <blockquote className="border-l-2 pl-6" style={{ borderColor: post.characterColor }}>
            <p className="font-['Special_Elite'] text-[#AAAAAA] text-lg md:text-xl leading-relaxed italic max-w-4xl">
              &ldquo;{post.excerpt}&rdquo;
            </p>
          </blockquote>
        </div>
      </div>

      {/* Main Content */}
      <main className="w-full px-6 py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

            {/* Chapter Navigation — Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-28">
                <div className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-[0.3em] uppercase mb-4">
                  [ Chapters ]
                </div>
                <div className="flex flex-col gap-1">
                  {post.chapters.map((ch, i) => (
                    <button
                      key={ch.id}
                      onClick={() => setActiveChapter(ch.id)}
                      className={`text-left px-4 py-3 border transition-all duration-300 cursor-pointer ${
                        activeChapter === ch.id
                          ? "border-[#8B1A1A]/60 bg-[#8B1A1A]/10"
                          : "border-[#1E1E1E] hover:border-[#333]"
                      }`}
                    >
                      <div
                        className="font-['Share_Tech_Mono'] text-xs tracking-widest uppercase mb-1"
                        style={{ color: activeChapter === ch.id ? post.characterColor : "#3A3A3A" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div
                        className={`font-['Rajdhani'] text-sm leading-snug transition-colors ${
                          activeChapter === ch.id ? "text-white" : "text-[#555]"
                        }`}
                      >
                        {ch.title.replace(/^Chapter [IVX]+ — /, "")}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Progress */}
                <div className="mt-6 border border-[#1E1E1E] p-4">
                  <div className="font-['Share_Tech_Mono'] text-[#333] text-xs tracking-widest uppercase mb-3">
                    Progress
                  </div>
                  <div className="h-1 bg-[#1A1A1A] w-full">
                    <div
                      className="h-1 transition-all duration-500"
                      style={{
                        width: `${((currentIndex + 1) / post.chapters.length) * 100}%`,
                        backgroundColor: post.characterColor,
                      }}
                    />
                  </div>
                  <div className="font-['Share_Tech_Mono'] text-[#333] text-xs mt-2">
                    {currentIndex + 1} / {post.chapters.length}
                  </div>
                </div>

                {/* Read time */}
                <div className="mt-3 px-4 py-3 border border-[#1A1A1A]">
                  <div className="font-['Share_Tech_Mono'] text-[#333] text-xs tracking-widest uppercase">
                    {post.readTime}
                  </div>
                </div>
              </div>
            </aside>

            {/* Chapter Content */}
            <article className="lg:col-span-3">
              {currentChapter && (
                <div key={currentChapter.id}>
                  <div
                    className="font-['Share_Tech_Mono'] text-xs tracking-[0.3em] uppercase mb-2"
                    style={{ color: post.characterColor }}
                  >
                    {post.entryNumber} &mdash; {post.timeline}
                  </div>
                  <h2 className="font-['Special_Elite'] text-white text-3xl md:text-4xl mb-8 pb-6 border-b border-[#1A1A1A]">
                    {currentChapter.title}
                  </h2>

                  <div className="space-y-4">
                    {currentChapter.content.split("\n\n").map((para, i) =>
                      renderParagraph(para.trim(), i, post.characterColor)
                    )}
                  </div>

                  {/* Chapter Nav Arrows */}
                  <div className="flex items-center justify-between mt-14 pt-8 border-t border-[#1A1A1A]">
                    <button
                      disabled={currentIndex === 0}
                      onClick={() => currentIndex > 0 && setActiveChapter(post.chapters[currentIndex - 1].id)}
                      className={`flex items-center gap-2 font-['Share_Tech_Mono'] text-xs tracking-[0.15em] uppercase transition-all duration-300 cursor-pointer px-4 py-2 border whitespace-nowrap ${
                        currentIndex === 0
                          ? "border-[#1A1A1A] text-[#2C2C2C] cursor-not-allowed"
                          : "border-[#2C2C2C] text-[#555] hover:text-[#C8922A] hover:border-[#C8922A]/40"
                      }`}
                    >
                      <i className="ri-arrow-left-line" />
                      Prev Chapter
                    </button>

                    {currentIndex < post.chapters.length - 1 ? (
                      <button
                        onClick={() => setActiveChapter(post.chapters[currentIndex + 1].id)}
                        className="flex items-center gap-2 font-['Share_Tech_Mono'] text-xs tracking-[0.15em] uppercase border px-4 py-2 transition-all duration-300 cursor-pointer whitespace-nowrap"
                        style={{ color: post.characterColor, borderColor: `${post.characterColor}40` }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = `${post.characterColor}10`;
                          (e.currentTarget as HTMLButtonElement).style.borderColor = post.characterColor;
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
                          (e.currentTarget as HTMLButtonElement).style.borderColor = `${post.characterColor}40`;
                        }}
                      >
                        Next Chapter
                        <i className="ri-arrow-right-line" />
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate("/lore")}
                        className="flex items-center gap-2 font-['Share_Tech_Mono'] text-xs tracking-[0.15em] uppercase border border-[#8B1A1A]/40 text-[#C8922A] hover:bg-[#8B1A1A]/10 px-4 py-2 transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        Back to Archive
                        <i className="ri-book-open-line" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
